/**
 * AddMediaItemDialog - Dialog para adicionar/editar item de mídia
 * 
 * Suporta: Imagem, Vídeo, Áudio, PTV, GIF, Documento
 * Permite selecionar da galeria ou inserir URL externa
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link, FolderOpen, Image, Video, Music, FileText, Circle, Film } from 'lucide-react';
import { MediaGalleryPicker } from '@/components/tenant/media/MediaGalleryPicker';
import { MessageItem, CampaignItemType, itemTypeConfig, allowedMimeTypes } from './types';
import { MediaFileType } from '@/types/media';

// =====================================================
// INTERFACES
// =====================================================

interface AddMediaItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    mediaUrl: string;
    mediaFilename?: string;
    mediaMimeType?: string;
    caption?: string;
  }) => void;
  mediaType: CampaignItemType;
  initialValue?: MessageItem | null;
  tenantId?: string;
}

// Mapeamento de CampaignItemType para MediaFileType da galeria
const mediaTypeToFileType: Record<CampaignItemType, MediaFileType[]> = {
  image: ['image'],
  video: ['video'],
  audio: ['audio'],
  ptv: ['video'],
  gif: ['image'], // GIFs são armazenados como image
  document: ['document'],
  text: [],
  delay: [],
  image_buttons: ['image'], // Imagem com botões usa tipos de imagem
  contact: [], // Contato não usa mídia
  template: [], // Template não usa mídia diretamente
};

// =====================================================
// COMPONENTE
// =====================================================

export function AddMediaItemDialog({
  open,
  onOpenChange,
  onSave,
  mediaType,
  initialValue,
  tenantId,
}: AddMediaItemDialogProps) {
  // Estado do formulário
  const [sourceType, setSourceType] = useState<'url' | 'gallery'>('gallery');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaFilename, setMediaFilename] = useState('');
  const [mediaMimeType, setMediaMimeType] = useState('');
  const [caption, setCaption] = useState('');
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);

  // Configuração do tipo atual
  const config = itemTypeConfig[mediaType];
  const allowedFileTypes = mediaTypeToFileType[mediaType];

  // Reset ao abrir dialog
  useEffect(() => {
    if (open) {
      if (initialValue) {
        setMediaUrl(initialValue.media_url || '');
        setMediaFilename(initialValue.media_filename || '');
        setMediaMimeType(initialValue.media_mime_type || '');
        setCaption(initialValue.text_content || '');
        // Determina se é URL externa ou galeria
        setSourceType(initialValue.media_url?.startsWith('http') ? 'url' : 'gallery');
      } else {
        setMediaUrl('');
        setMediaFilename('');
        setMediaMimeType('');
        setCaption('');
        setSourceType('gallery');
      }
      setShowGalleryPicker(false);
    }
  }, [open, initialValue]);

  // Handler de salvar
  const handleSave = () => {
    if (mediaUrl.trim()) {
      onSave({
        mediaUrl: mediaUrl.trim(),
        mediaFilename: mediaFilename || undefined,
        mediaMimeType: mediaMimeType || undefined,
        caption: caption.trim() || undefined,
      });
    }
  };

  // Handler de seleção da galeria
  const handleGallerySelect = (media: any) => {
    setMediaUrl(media.storage_path);
    setMediaFilename(media.name);
    setMediaMimeType(media.mime_type);
    setShowGalleryPicker(false);
  };

  // Validação
  const isValid = mediaUrl.trim().length > 0;

  // Ícone baseado no tipo
  const getIcon = () => {
    switch (mediaType) {
      case 'image': return <Image className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      case 'audio': return <Music className="h-4 w-4" />;
      case 'ptv': return <Circle className="h-4 w-4" />;
      case 'gif': return <Film className="h-4 w-4" />;
      case 'document': return <FileText className="h-4 w-4" />;
      default: return null;
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getIcon()}
            {initialValue ? `Editar ${config.label}` : `Adicionar ${config.label}`}
          </DialogTitle>
          <DialogDescription>
            Selecione um arquivo da galeria ou insira uma URL externa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Tabs para escolher fonte */}
          <Tabs value={sourceType} onValueChange={(v) => setSourceType(v as 'url' | 'gallery')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="gallery" className="gap-2">
                <FolderOpen className="h-4 w-4" />
                Galeria de Mídia
              </TabsTrigger>
              <TabsTrigger value="url" className="gap-2">
                <Link className="h-4 w-4" />
                URL Externa
              </TabsTrigger>
            </TabsList>

            {/* Galeria */}
            <TabsContent value="gallery" className="space-y-4 mt-4">
              {showGalleryPicker ? (
                <MediaGalleryPicker
                  allowedTypes={allowedFileTypes}
                  onSelect={handleGallerySelect}
                  onCancel={() => setShowGalleryPicker(false)}
                  currentSelection={undefined}
                />
              ) : (
                <div className="space-y-4">
                  {mediaUrl && !mediaUrl.startsWith('http') ? (
                    <div className="p-4 border rounded-lg bg-muted/50">
                      <p className="text-sm font-medium mb-1">Arquivo selecionado:</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {mediaFilename || mediaUrl}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => setShowGalleryPicker(true)}
                      >
                        Trocar arquivo
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full h-24"
                      onClick={() => setShowGalleryPicker(true)}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <FolderOpen className="h-8 w-8 text-muted-foreground" />
                        <span>Selecionar da Galeria</span>
                      </div>
                    </Button>
                  )}
                </div>
              )}
            </TabsContent>

            {/* URL Externa */}
            <TabsContent value="url" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="media-url">URL do Arquivo *</Label>
                <Input
                  id="media-url"
                  value={sourceType === 'url' ? mediaUrl : ''}
                  onChange={(e) => {
                    setMediaUrl(e.target.value);
                    setMediaFilename('');
                    setMediaMimeType('');
                  }}
                  placeholder="https://exemplo.com/arquivo.jpg"
                />
                <p className="text-xs text-muted-foreground">
                  Cole a URL pública do arquivo que deseja enviar.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          {/* Campo de legenda (quando permitido) - oculto durante seleção na galeria */}
          {config.allowCaption && !showGalleryPicker && (
            <div className="space-y-2">
              <Label htmlFor="caption">Legenda (opcional)</Label>
              <Textarea
                id="caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Digite uma legenda para a mídia..."
                rows={3}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Use <code className="bg-muted px-1 rounded">{'{{nome}}'}</code> para 
                inserir o nome do contato.
              </p>
            </div>
          )}
        </div>

        {/* Footer oculto durante seleção na galeria (picker tem botões próprios) */}
        {!showGalleryPicker && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!isValid}>
              {initialValue ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
